import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router'
import { api, ErroreApi } from '../servizi/api.ts'
import { useAree, useCatalogo, useSessione } from '../servizi/sessione.tsx'
import {
  AreaTesto, Bottone, Campo, Etichetta, EtichettaArea, Modale, Riquadro, Scelta, Spunta, Tabella,
  type Opzione, type TonoEtichetta,
} from '../componenti/base.tsx'
import { Attesa, Avviso, Caselle, Dato, Intestazione, Vuoto } from '../componenti/Pezzi.tsx'
import { IconaIndietro, IconaPiu } from '../componenti/Icone.tsx'
import { SceltaComune, type ComuneScelto } from './Ente.tsx'
import { SceltaEnte, aiutoRicerca, comeArrivano, useTrovati, type Ente as EnteScelto } from './Registro.tsx'
import { useFuocoSulPrimoErrore } from '../componenti/persona-campi.tsx'
import { AvvisoModulo, erroriDellApi, type Etichette } from '../componenti/errori-modulo.tsx'
import { LoghiProgetto } from './LoghiStampa.tsx'
import { ModuloDestinatario } from './ModuloDestinatario.tsx'
import { vociSedi } from '../servizi/sedi.ts'
import { PostaDelProgetto } from './posta/PostaDelProgetto.tsx'
import {
  SceltaCasellaProgetto, casellaIniziale, controllaCasella, corpoCasella, type Candidato, type ValoreCasella,
} from './posta/CaselleCondivise.tsx'

/**
 * Scheda di un progetto (D-24): dati generali, periodo e importi, enti, territorio e sedi CIDIS,
 * destinatari, work package con attivita' e sottoattivita', scadenze di rendicontazione. Ogni sezione
 * si modifica per conto suo. I comandi seguono i permessi che l'API calcola sul progetto (`permessi`),
 * non quelli della sessione: un incarico di area gestisce i progetti della sua area e non gli altri.
 * Lo stato si calcola dalle date e non si corregge a mano.
 */

export type StatoProgetto = 'da_avviare' | 'in_corso' | 'concluso'
type Persona = { id: string; nome: string; cognome: string }
type EnteBreve = { id: string; nome: string; sigla: string | null; attivo: boolean }
type Partner = {
  stakeholder_id: string; nome: string; sigla: string | null; attivo: boolean; ruolo: string; ruolo_descrizione: string | null
}
type StatoAttivita = 'da_iniziare' | 'in_corso' | 'sospesa' | 'conclusa'
type Attivita = {
  id: string; wp_id: string; padre_id: string | null; codice: string | null; titolo: string
  stato: StatoAttivita; avanzamento: number; data_inizio: string | null; data_fine: string | null
  indicatore_nome: string | null; indicatore_target: number | null; indicatore_unita: string | null
  ore_previste: number | null; ore_svolte: number | null; archiviata_il: string | null
  responsabile: Persona | null; sottoattivita?: Attivita[]
}
type Wp = {
  id: string; codice: string; titolo: string; descrizione: string | null
  data_inizio: string | null; data_fine: string | null; archiviato_il: string | null
  tipologie: { codice: string; nome: string; area_codice: string }[]; avanzamento: number | null; attivita: Attivita[]
}
type StatoScadenza = 'da_fare' | 'in_corso' | 'consegnata'
type Scadenza = {
  id: string; descrizione: string; scadenza: string; stato: StatoScadenza; promemoria_giorni: number
  consegnata_il: string | null; responsabile: Persona | null; scaduta: boolean; in_arrivo: boolean
}
export type DettaglioProgetto = {
  id: string; titolo: string; codice_finanziatore: string | null; cup: string | null; cig: string | null
  codice_cidis: string | null; fondo_codice: string | null; fondo_nome: string | null
  fondo_richiede_testo: boolean | null; fondo_testo: string | null; fondo_storico: string | null
  area_principale: string | null; area_nome: string | null; finalita: string | null
  data_inizio: string | null; data_fine: string | null; stato: StatoProgetto | null
  importo_finanziato: number | null; importo_cofinanziamento: number | null
  periodicita_rendicontazione: string | null; periodicita_nome: string | null
  referente: (Persona & { attivo: boolean }) | null
  capofila: EnteBreve | null; finanziatore: EnteBreve | null; soggetto_attuatore: EnteBreve | null
  obiettivo_specifico: string | null; obiettivo_nazionale: string | null; data_saldo: string | null
  ruolo_privacy: string | null; controlli_in_corso: boolean
  legacy_id: number | null; creato_il: string; aggiornato_il: string | null
  partner: Partner[]; comuni: (ComuneScelto & { soppresso_il: string | null })[]
  sedi: { id: string; codice: string; nome: string; attiva: boolean }[]
  destinatari: { categoria: string; nome: string; attivo: boolean; target: number | null }[]
  destinatari_testo: string | null
  wp: Wp[]; scadenze: Scadenza[]
  permessi: { gestisci: boolean; rendiconta: boolean; assegna: boolean }
  /** N-1: il referente vede davvero i dati del progetto, o la richiesta e’ ancora aperta? */
  referente_accesso: {
    stato: 'concesso' | 'in_attesa' | 'assente'
    incarico_id: string | null; scadenza: string | null; da_questa_domanda: boolean
    approvazione_id: string | null; tipo_richiesta: string | null
  } | null
}

/** Esito di una delle due domande sull'accesso del referente, come lo restituisce l'API. */
export type EsitoAccessoReferente = {
  azione: 'concessione' | 'revoca'
  esito: 'concesso' | 'revocato' | 'richiesto' | 'non_richiesto'
  messaggio: string
  incarichi_non_toccati?: { id: string; ruolo: string }[]
}
type VoceFondo = { codice: string; nome: string; richiede_testo: boolean; attivo: boolean }

// --- aiuti -------------------------------------------------------------------------------------------

export const STATI_PROGETTO: Record<StatoProgetto, { etichetta: string; tono: TonoEtichetta }> = {
  da_avviare: { etichetta: 'da avviare', tono: 'informativo' },
  in_corso: { etichetta: 'in corso', tono: 'positivo' },
  concluso: { etichetta: 'concluso', tono: 'neutro' },
}
const STATI_ATTIVITA: Record<StatoAttivita, { etichetta: string; tono: TonoEtichetta }> = {
  da_iniziare: { etichetta: 'da iniziare', tono: 'neutro' },
  in_corso: { etichetta: 'in corso', tono: 'informativo' },
  sospesa: { etichetta: 'sospesa', tono: 'attenzione' },
  conclusa: { etichetta: 'conclusa', tono: 'positivo' },
}
const STATI_SCADENZA: Record<StatoScadenza, { etichetta: string; tono: TonoEtichetta }> = {
  da_fare: { etichetta: 'da fare', tono: 'neutro' },
  in_corso: { etichetta: 'in corso', tono: 'informativo' },
  consegnata: { etichetta: 'consegnata', tono: 'positivo' },
}
const opzioniDa = <T extends string>(stati: Record<T, { etichetta: string }>): Opzione[] =>
  (Object.keys(stati) as T[]).map((k) => ({ valore: k, etichetta: stati[k].etichetta }))

/** Ruoli dei partner: capofila e finanziatore hanno un campo proprio. */
export const RUOLI_PARTNER: Opzione[] = [
  { valore: 'partner', etichetta: 'Partner' },
  { valore: 'rete', etichetta: 'Ente della rete' },
]
const nomeRuolo = (ruolo: string) => RUOLI_PARTNER.find((r) => r.valore === ruolo)?.etichetta ?? ruolo

// Il ruolo privacy di CIDIS nel progetto (`core.progetto.ruolo_privacy`, valori in 060) non si
// chiede piu' dal modulo ne' si mostra nella scheda: scelta del 26/09/2026 sulle Osservazioni 3,
// punto 4. La colonna resta, con i valori gia' scritti. Chi la reintroduce ricrea qui la tabella
// dei nomi leggibili.

/** Le date del progetto sono giorni senza ora: si scrivono senza passare dal fuso del browser. */
export const dataBreve = (v: string | null | undefined) => (v ? v.slice(0, 10).split('-').reverse().join('/') : '—')
const momento = (v: string | null | undefined) =>
  (v ? new Date(v).toLocaleString('it-IT', { dateStyle: 'short', timeStyle: 'short' }) : '—')
export const euro = (v: number | null | undefined) =>
  (v == null ? null : Number(v).toLocaleString('it-IT', { style: 'currency', currency: 'EUR' }))
const cifre = (v: number | null | undefined) =>
  (v == null ? null : Number(v).toLocaleString('it-IT', { maximumFractionDigits: 2 }))
const oggiRoma = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Rome' })
const nomePersona = (p: Persona | null | undefined) => (p ? `${p.nome} ${p.cognome}` : null)
const nomeEnte = (e: { nome: string; sigla: string | null }) => (e.sigla ? `${e.sigla} — ${e.nome}` : e.nome)
const compatto = (v: string) => v.replace(/\s+/g, '').toUpperCase()

/** Stessa regola di core.stato_progetto: serve all'anteprima nel modulo. */
export function statoDalleDate(inizio: string | null, fine: string | null, oggi = oggiRoma()): StatoProgetto | null {
  if (!inizio && !fine) return null
  if (inizio && inizio > oggi) return 'da_avviare'
  if (fine && fine < oggi) return 'concluso'
  return 'in_corso'
}

export function StatoDelProgetto({ stato }: { stato: StatoProgetto | null }) {
  return stato
    ? <Etichetta tono={STATI_PROGETTO[stato].tono}>{STATI_PROGETTO[stato].etichetta}</Etichetta>
    : <span className="tenue">date non indicate</span>
}

export function messaggioDi(e: unknown, fuoriPerimetro: string): string {
  if (!(e instanceof ErroreApi)) return 'Non è andata a buon fine: controlla la connessione e riprova.'
  return e.stato === 404 ? fuoriPerimetro : e.message
}

/** Aggiunge alle opzioni il valore gia' registrato quando non compare fra quelle attive. */
function conVoce(opzioni: Opzione[], valore: string | null | undefined, etichetta: string | null | undefined) {
  if (!valore || opzioni.some((o) => o.valore === valore)) return opzioni
  return [{ valore, etichetta: `${etichetta ?? valore} · non più in uso` }, ...opzioni]
}

/** Importi in euro: cifre con al piu' due decimali, virgola o punto, senza separatore delle migliaia. */
const IMPORTO = /^\d{1,10}([.,]\d{1,2})?$/
const AIUTO_IMPORTO = 'Cifre con al massimo due decimali, senza separatore delle migliaia: 420000,50.'
const ORE = /^\d{1,6}([.,]\d{1,2})?$/
const numeroDa = (v: string) => (v.trim() ? Number(v.trim().replace(',', '.')) : null)
const testoDi = (v: number | null | undefined) => (v == null ? '' : String(v).replace('.', ','))
const uguali = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b)

/** Elenco di appoggio letto una volta: in errore resta vuoto. */
function useVoci<T>(percorso: string): T[] {
  const [voci, setVoci] = useState<T[]>([])
  useEffect(() => {
    let valido = true
    api.get<T[]>(percorso).then((r) => { if (valido) setVoci(r) }).catch(() => { if (valido) setVoci([]) })
    return () => { valido = false }
  }, [percorso])
  return voci
}

/** Etichette dei campi dei moduli del progetto, per gli errori che l'API restituisce con il codice del campo. */
const ETICHETTE_PROGETTO: Etichette = {
  titolo: 'Titolo', codice_cidis: 'Codice CIDIS del progetto', codice_finanziatore: 'Codice (del finanziatore)',
  cup: 'CUP', cig: 'CIG', fondo_codice: 'Fondo', fondo_testo: 'Specifica del fondo', area_principale: 'Area principale',
  finalita: 'Obiettivo o finalità generale', obiettivo_specifico: 'Obiettivo specifico FAMI',
  obiettivo_nazionale: 'Obiettivo nazionale FAMI', ruolo_privacy: 'Ruolo privacy di CIDIS', referente_id: 'Referente CIDIS',
  data_inizio: 'Data inizio', data_fine: 'Data fine', importo_finanziato: 'Importo finanziato',
  importo_cofinanziamento: 'Importo cofinanziamento', periodicita_rendicontazione: 'Periodicità di rendicontazione',
  data_saldo: 'Data del saldo', controlli_in_corso: 'Controlli del finanziatore in corso', sedi: 'Sedi CIDIS', comuni: 'Comuni di realizzazione delle attività',
  capofila_id: 'Ente capofila', finanziatore_id: 'Ente finanziatore', soggetto_attuatore: 'Soggetto attuatore',
  partner: 'Partner', destinatari: 'Destinatari', codice: 'Codice', descrizione: 'Descrizione',
  tipologie: 'Tipologie di intervento', responsabile_id: 'Responsabile', indicatore_nome: 'Indicatore',
  indicatore_target: 'Valore obiettivo', indicatore_unita: 'Unità', ore_previste: 'Ore previste', stato: 'Stato',
  avanzamento: 'Avanzamento (%)', ore_svolte: 'Ore svolte', scadenza: 'Data della scadenza',
  promemoria_giorni: 'Preavviso del promemoria (giorni)', padre_id: 'Attività', preavviso: 'Preavviso del promemoria (giorni)',
  ore: 'Ore svolte',
}

/** Errori dell'API senza dettagli, riconosciuti dal codice e portati sul campo a cui si riferiscono. */
const CODICI_CAMPO_PROGETTO: Record<string, string> = {
  AREA: 'area_principale', CIG: 'cig', COMUNE: 'comuni', CUP: 'cup', DESTINATARI: 'destinatari', FONDO: 'fondo_codice',
  FONDO_TESTO: 'fondo_testo', PADRE: 'padre_id', PARTNER: 'partner', PERIODICITA: 'periodicita_rendicontazione',
  PERIODO: 'data_fine', SEDE: 'sedi', TIPOLOGIA: 'tipologie', CODICE_CIDIS_DUPLICATO: 'codice_cidis', WP_CODICE: 'codice',
}

/**
 * Invio di un modulo: errore leggibile, errori dell'API sui campi, comando non ripetibile durante
 * l'operazione. Dopo ogni invio non riuscito, anche per i controlli dell'interfaccia (`rifiuta`), il
 * fuoco va sul primo campo segnalato del modulo collegato a `radice`.
 */
function useInvio(salvato: () => void) {
  const [errore, setErrore] = useState<string | null>(null)
  const [erroriApi, setErroriApi] = useState<Record<string, string>>({})
  const [inCorso, setInCorso] = useState(false)
  const [tentativo, setTentativo] = useState(0)
  const radice = useRef<HTMLFormElement>(null)
  useFuocoSulPrimoErrore(radice, tentativo)

  function rifiuta(messaggio: string) {
    setErrore(messaggio)
    setErroriApi({})
    setTentativo((n) => n + 1)
  }
  async function invia(azione: () => Promise<unknown>) {
    setErrore(null)
    setErroriApi({})
    setInCorso(true)
    try {
      await azione()
      salvato()
    } catch (e) {
      setErrore(messaggioDi(e, 'Con i tuoi incarichi non puoi farlo.'))
      setErroriApi(erroriDellApi(e, CODICI_CAMPO_PROGETTO))
      setTentativo((n) => n + 1)
    } finally {
      setInCorso(false)
    }
  }
  return { errore, erroriApi, inCorso, invia, rifiuta, radice }
}

/**
 * Comandi in coda al modulo, con l'esito dell'invio subito sopra: nella finestra lunga un avviso in
 * testa resterebbe fuori dalla vista di chi ha appena premuto il comando.
 */
function AzioniModulo({ annulla, inCorso, etichetta, errore = null, errori = {} }: {
  annulla: () => void; inCorso: boolean; etichetta: string; errore?: string | null; errori?: Record<string, string>
}) {
  return (
    <>
      <AvvisoModulo errore={errore} errori={errori} etichette={ETICHETTE_PROGETTO} />
      <div className="azioni-modulo">
        <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
        <Bottone variante="primario" tipo="submit" inCorso={inCorso}>{etichetta}</Bottone>
      </div>
    </>
  )
}

function Avanzamento({ valore }: { valore: number | null }) {
  if (valore == null) return <span className="tenue">—</span>
  return (
    <span className="rete-avanzamento-riga">
      <progress className="rete-avanzamento" max={100} value={valore} aria-label={`Avanzamento ${valore}%`} />
      <span className="mono piccolo">{valore}%</span>
    </span>
  )
}

/**
 * Scelta di un utente di sistema per nome: referente CIDIS del progetto o responsabile di un'attivita'
 * o di una scadenza. L'utente gia' scelto resta fra i candidati.
 */
export function SceltaUtente({ etichetta, etichettaRicerca, valore, cambia, vuota, aiuto, errore }: {
  etichetta: string; valore: Persona | null; cambia: (p: Persona | null) => void; vuota: string; aiuto?: ReactNode
  /** Etichetta del campo di ricerca; senza, «Cerca» e l'etichetta con la sola iniziale minuscola. */
  etichettaRicerca?: string
  errore?: string | null
}) {
  const [testo, setTesto] = useState('')
  // Elenco caricato subito: gli utenti interni sono poche decine, e una tendina che resta vuota
  // finche' non si indovinano due lettere sembra rotta (Osservazioni 3, punto 3).
  const ricerca = useTrovati<Persona[], Persona>(testo, percorsoUtenti, comeArrivano, 300, 0)
  const { trovati } = ricerca
  const candidati = valore && !trovati.some((u) => u.id === valore.id) ? [valore, ...trovati] : trovati
  return (
    <div className="rete-ricerca rete-ricerca-piena">
      {/* Solo l'iniziale in minuscolo: una sigla come CIDIS resta com'e', anche per gli ausili. */}
      <Campo etichetta={etichettaRicerca ?? `Cerca ${etichetta.charAt(0).toLowerCase()}${etichetta.slice(1)}`}
             tipo="search" valore={testo} cambia={setTesto} segnaposto="Nome o cognome, per restringere l’elenco" />
      <Scelta etichetta={etichetta} vuota={vuota} valore={valore?.id ?? ''} errore={errore}
              opzioni={candidati.map((u) => ({ valore: u.id, etichetta: `${u.cognome} ${u.nome}` }))}
              cambia={(id) => cambia(candidati.find((u) => u.id === id) ?? null)}
              aiuto={aiutoRicerca(ricerca, 'Nessun utente interno attivo con questo nome.', aiuto)} />
    </div>
  )
}

// Senza testo si chiede l'elenco: `cerca` vuoto sarebbe rifiutato dall'API, che ne vuole due lettere.
// `tipo=interno` lascia solo chi puo' essere referente o responsabile: offrire gli altri era un inganno,
// perche' l'API li rifiuta al salvataggio.
const percorsoUtenti = (termine: string) =>
  `/progetti/utenti-selezionabili?tipo=interno${termine ? `&cerca=${encodeURIComponent(termine)}` : ''}`

// --- scheda del progetto ----------------------------------------------------------------------------

type Finestra =
  | { tipo: 'generali' | 'periodo' | 'enti' | 'territorio' | 'destinatari' }
  | { tipo: 'wp'; wp?: Wp }
  | { tipo: 'attivita'; wp: Wp; attivita?: Attivita; padre?: Attivita }
  | { tipo: 'andamento'; attivita: Attivita }
  | { tipo: 'scadenza'; scadenza?: Scadenza }
  | { tipo: 'stato-scadenza'; scadenza: Scadenza }
  | { tipo: 'conferma'; titolo: string; testo: string; comando: string; percorso: string; metodo: 'post' | 'elimina' }

export function Progetto() {
  const { id } = useParams()
  const aree = useAree()
  const { puo } = useSessione()
  const [progetto, setProgetto] = useState<DettaglioProgetto | null>(null)
  const [errore, setErrore] = useState<string | null>(null)
  const [erroreAzione, setErroreAzione] = useState<string | null>(null)
  const [mostraTolti, setMostraTolti] = useState(false)
  const [finestra, setFinestra] = useState<Finestra | null>(null)
  const [inCorso, setInCorso] = useState(false)
  const [esito, setEsito] = useState<string | null>(null)

  const carica = useCallback(() => {
    setErrore(null)
    api.get<DettaglioProgetto>(`/progetti/${id}${mostraTolti ? '?archiviati=true' : ''}`)
      .then(setProgetto)
      .catch((e) => setErrore(messaggioDi(e, 'Progetto non trovato, oppure non rientra nei tuoi incarichi.')))
  }, [id, mostraTolti])
  useEffect(carica, [carica])

  const chiudi = () => setFinestra(null)
  // Il messaggio arriva dalle domande sul referente: dice se l'accesso si e' aperto subito o se
  // e' partita una richiesta di approvazione (N-1).
  const salvato = (_id?: string, messaggio?: string) => {
    setFinestra(null)
    setEsito(messaggio ?? null)
    carica()
  }

  async function conferma(f: Extract<Finestra, { tipo: 'conferma' }>) {
    setErroreAzione(null)
    setInCorso(true)
    try {
      if (f.metodo === 'elimina') await api.elimina(f.percorso)
      else await api.post(f.percorso)
      salvato()
    } catch (e) {
      setFinestra(null)
      setErroreAzione(messaggioDi(e, 'Con i tuoi incarichi non puoi farlo.'))
    } finally {
      setInCorso(false)
    }
  }

  const p = progetto
  const gestisce = !!p?.permessi.gestisci
  const rendiconta = !!p?.permessi.rendiconta
  const comandoModifica = (tipo: 'generali' | 'periodo' | 'enti' | 'territorio' | 'destinatari') => (gestisce
    ? <Bottone variante="secondario" dimensione="piccolo" onClick={() => setFinestra({ tipo })}>Modifica</Bottone>
    : undefined)
  const partner = (p?.partner ?? [])

  return (
    <>
      <Intestazione
        titolo={p?.titolo ?? 'Progetto'}
        sottotitolo={p ? (
          <span className="rete-con-etichetta">
            {[p.codice_cidis, p.codice_finanziatore && `codice del finanziatore ${p.codice_finanziatore}`].filter(Boolean).join(' · ')}
            <StatoDelProgetto stato={p.stato} />
          </span>
        ) : undefined}
        azioni={<Link className="bottone bottone-discreto" to="/progetti"><IconaIndietro /> Progetti</Link>}
      />

      {errore && <Avviso tipo="errore">{errore}</Avviso>}
      {!errore && !p && <Attesa />}
      {erroreAzione && <Avviso tipo="errore">{erroreAzione}</Avviso>}
      {esito && <Avviso tipo="successo">{esito}</Avviso>}

      {p && (
        <>
          {p.fondo_storico && !p.fondo_codice && (
            <Avviso tipo="attenzione">
              Il fondo è indicato solo in testo, nel campo della versione precedente: «{p.fondo_storico}».
              {gestisce ? ' Scegliere la voce del catalogo con «Modifica» nei dati generali.' : ''}
            </Avviso>
          )}

          <div className="rete-dettaglio">
            <Riquadro titolo="Dati generali" azioni={comandoModifica('generali')}>
              <dl className="rete-dati">
                <Dato nome="Titolo">{p.titolo}</Dato>
                <Dato nome="Codice CIDIS">{p.codice_cidis}</Dato>
                <Dato nome="Codice del finanziatore">{p.codice_finanziatore}</Dato>
                <Dato nome="CUP">{p.cup ? <span className="mono">{p.cup}</span> : null}</Dato>
                <Dato nome="CIG">{p.cig ? <span className="mono">{p.cig}</span> : null}</Dato>
                <Dato nome="Fondo">{p.fondo_nome ? `${p.fondo_nome}${p.fondo_testo ? `: ${p.fondo_testo}` : ''}` : null}</Dato>
                <Dato nome="Area principale">
                  {p.area_principale
                    ? (
                      <span className="rete-tipologia">
                        <EtichettaArea codice={p.area_principale} colore={aree.coloreDi(p.area_principale)} />
                        {p.area_nome}
                      </span>
                    )
                    : null}
                </Dato>
                <Dato nome="Obiettivo o finalità generale">
                  {p.finalita ? <span className="testo-libero">{p.finalita}</span> : null}
                </Dato>
                <Dato nome="Referente CIDIS">
                  {p.referente
                    ? (
                      <span className="rete-con-etichetta">
                        {nomePersona(p.referente)}
                        {!p.referente.attivo && <Etichetta tono="attenzione">utente non attivo</Etichetta>}
                        {/* N-1: essere referente non apre i dati del progetto. Dire se li vede o
                            no toglie l'equivoco a chi legge la scheda. */}
                        <StatoAccessoReferente accesso={p.referente_accesso} />
                      </span>
                    )
                    : null}
                </Dato>
              </dl>
            </Riquadro>

            <Riquadro titolo="Periodo, importi e rendicontazione" azioni={comandoModifica('periodo')}>
              <dl className="rete-dati">
                <Dato nome="Data inizio">{p.data_inizio ? dataBreve(p.data_inizio) : null}</Dato>
                <Dato nome="Data fine">{p.data_fine ? dataBreve(p.data_fine) : null}</Dato>
                <Dato nome="Stato">{<StatoDelProgetto stato={p.stato} />}</Dato>
                <Dato nome="Importo finanziato">{euro(p.importo_finanziato)}</Dato>
                <Dato nome="Importo cofinanziamento">{euro(p.importo_cofinanziamento)}</Dato>
                <Dato nome="Periodicità di rendicontazione">{p.periodicita_nome}</Dato>
              </dl>
            </Riquadro>
          </div>

          <div className="rete-dettaglio">
            <Riquadro titolo="Enti del progetto" azioni={comandoModifica('enti')}>
              <dl className="rete-dati">
                <Dato nome="Ente capofila">{p.capofila ? <EnteCollegato ente={p.capofila} /> : null}</Dato>
                <Dato nome="Ente finanziatore">{p.finanziatore ? <EnteCollegato ente={p.finanziatore} /> : null}</Dato>
              </dl>
              <h3 className="rete-titoletto">Partner</h3>
              {partner.length
                ? (
                  <Tabella intestazioni={['Ente', 'Ruolo', 'Ruolo nel progetto']}>
                    {partner.map((x) => (
                      <tr key={`${x.stakeholder_id}:${x.ruolo}`}>
                        <td data-etichetta="Ente">
                          <EnteCollegato ente={{ id: x.stakeholder_id, nome: x.nome, sigla: x.sigla, attivo: x.attivo }} />
                        </td>
                        <td data-etichetta="Ruolo">{nomeRuolo(x.ruolo)}</td>
                        <td data-etichetta="Ruolo nel progetto">{x.ruolo_descrizione ?? <span className="tenue">—</span>}</td>
                      </tr>
                    ))}
                  </Tabella>
                )
                : <p className="tenue">Nessun partner registrato.</p>}
            </Riquadro>

            <Riquadro titolo="Territorio e sedi CIDIS" azioni={comandoModifica('territorio')}>
              <h3 className="rete-titoletto">Comuni di realizzazione delle attività</h3>
              {p.comuni.length
                ? (
                  <div className="riga-etichette">
                    {p.comuni.map((c) => (
                      <Etichetta key={c.codice_istat} tono={c.soppresso_il ? 'attenzione' : 'neutro'}>
                        {c.nome}{c.provincia_sigla ? ` (${c.provincia_sigla})` : ''}{c.soppresso_il ? ' · soppresso' : ''}
                      </Etichetta>
                    ))}
                  </div>
                )
                : <p className="tenue">Nessun comune indicato.</p>}
              <h3 className="rete-titoletto">Sedi CIDIS</h3>
              {p.sedi.length
                ? (
                  <div className="riga-etichette">
                    {p.sedi.map((s) => (
                      <Etichetta key={s.id} tono={s.attiva ? 'accento' : 'neutro'}>{s.nome}{s.attiva ? '' : ' · non attiva'}</Etichetta>
                    ))}
                  </div>
                )
                : <p className="tenue">Nessuna sede CIDIS collegata.</p>}
            </Riquadro>
          </div>

          <Riquadro titolo="Destinatari" azioni={comandoModifica('destinatari')} denso>
            {p.destinatari_testo
              ? <p className="rete-testo">{p.destinatari_testo}</p>
              : <Vuoto testo="Destinatari non indicati." />}
            {/* Conteggio per categoria del vecchio modulo: non si chiede piu' (Osservazioni 3,
                punto 6), ma dove qualcuno l'ha scritto resta leggibile. */}
            {p.destinatari.length > 0 && (
              <Tabella intestazioni={['Categoria', 'Numero obiettivo']} vuoto={null}>
                {p.destinatari.map((d) => (
                  <tr key={d.categoria}>
                    <td data-etichetta="Categoria">{d.nome}{d.attivo ? '' : ' · non più in uso'}</td>
                    <td data-etichetta="Numero obiettivo" className="mono">{d.target ?? '—'}</td>
                  </tr>
                ))}
              </Tabella>
            )}
          </Riquadro>

          <Riquadro titolo="Work package e attività" azioni={gestisce
            ? <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                       onClick={() => setFinestra({ tipo: 'wp' })}>Aggiungi una WP</Bottone>
            : undefined}>
            {/* La spunta sta nel corpo: nell'intestazione, accanto al comando, su schermo stretto l'etichetta
                si spezzava su piu' righe e il suo margine disallineava l'intestazione. */}
            <Spunta etichetta="Mostra WP e attività tolte" valore={mostraTolti} cambia={setMostraTolti} />
            {p.wp.length === 0
              ? <Vuoto testo="Nessuna WP registrata." />
              : p.wp.map((w) => (
                <BloccoWp key={w.id} wp={w} gestisce={gestisce} rendiconta={rendiconta} apri={setFinestra} />
              ))}
          </Riquadro>

          <Riquadro titolo="Scadenze di rendicontazione" denso azioni={gestisce
            ? <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />}
                       onClick={() => setFinestra({ tipo: 'scadenza' })}>Aggiungi una scadenza</Bottone>
            : undefined}>
            <p className="rete-nota-riquadro">
              Periodicità: {p.periodicita_nome ?? 'non indicata'}. Il promemoria arriva al referente CIDIS e al
              responsabile della scadenza nei giorni di preavviso.
            </p>
            <Tabella intestazioni={['Scadenza', 'Descrizione', 'Responsabile', 'Preavviso', 'Stato',
                                    ...((gestisce || rendiconta) ? [<span className="solo-lettore" key="azioni">Azioni</span>] : [])]}
                     vuoto={<Vuoto testo="Nessuna scadenza registrata." />}>
              {p.scadenze.map((s) => (
                <tr key={s.id}>
                  <td data-etichetta="Scadenza">
                    <span className="rete-con-etichetta">
                      <span className="mono">{dataBreve(s.scadenza)}</span>
                      {s.scaduta && <Etichetta tono="critico">scaduta</Etichetta>}
                      {s.in_arrivo && <Etichetta tono="attenzione">in arrivo</Etichetta>}
                    </span>
                  </td>
                  <td data-etichetta="Descrizione">{s.descrizione}</td>
                  <td data-etichetta="Responsabile">{nomePersona(s.responsabile) ?? <span className="tenue">—</span>}</td>
                  <td data-etichetta="Preavviso" className="mono">{s.promemoria_giorni} gg</td>
                  <td data-etichetta="Stato">
                    <Etichetta tono={STATI_SCADENZA[s.stato].tono}>{STATI_SCADENZA[s.stato].etichetta}</Etichetta>
                  </td>
                  {(gestisce || rendiconta) && (
                    <td data-etichetta="Azioni">
                      <span className="riga riga-a-destra">
                        {rendiconta && (
                          <Bottone variante="discreto" dimensione="piccolo"
                                   onClick={() => setFinestra({ tipo: 'stato-scadenza', scadenza: s })}>Stato</Bottone>
                        )}
                        {gestisce && (
                          <Bottone variante="discreto" dimensione="piccolo"
                                   onClick={() => setFinestra({ tipo: 'scadenza', scadenza: s })}>Modifica</Bottone>
                        )}
                        {gestisce && s.stato !== 'consegnata' && (
                          <Bottone variante="discreto" dimensione="piccolo" onClick={() => setFinestra({
                            tipo: 'conferma', titolo: 'Elimina la scadenza', comando: 'Elimina la scadenza',
                            testo: `La scadenza «${s.descrizione}» del ${dataBreve(s.scadenza)} esce dal progetto e non genera più promemoria.`,
                            percorso: `/scadenze/${s.id}`, metodo: 'elimina',
                          })}>Elimina</Bottone>
                        )}
                      </span>
                    </td>
                  )}
                </tr>
              ))}
            </Tabella>
          </Riquadro>

          {/* 504: la casella di posta del progetto, chi accede e con che livello. */}
          <PostaDelProgetto progettoId={p.id} gestisceUtenze={puo('utente.gestisci')} />

          <LoghiProgetto progettoId={p.id} />

          {/* 492: il modulo che le persone del progetto firmano, stampato compilato dalla scheda. */}
          <ModuloDestinatario progettoId={p.id} />

          <Riquadro titolo="Registrazione e altri dati">
            <dl className="rete-dati">
              <Dato nome="Inserito il">{momento(p.creato_il)}</Dato>
              {p.aggiornato_il && <Dato nome="Ultima modifica">{momento(p.aggiornato_il)}</Dato>}
              {p.legacy_id !== null && <Dato nome="Progetto nel vecchio archivio">n. {p.legacy_id}</Dato>}
            </dl>
          </Riquadro>
        </>
      )}

      {p && finestra?.tipo === 'generali' && (
        <Modale titolo="Modifica i dati generali" larga chiudi={chiudi}>
          <ModuloProgetto iniziale={p} sezioni={['generali']} annulla={chiudi} salvato={salvato} />
        </Modale>
      )}
      {p && finestra?.tipo === 'periodo' && (
        <Modale titolo="Modifica periodo, importi e rendicontazione" larga chiudi={chiudi}>
          <ModuloProgetto iniziale={p} sezioni={['periodo']} annulla={chiudi} salvato={salvato} />
        </Modale>
      )}
      {p && finestra?.tipo === 'enti' && (
        <Modale titolo="Modifica gli enti del progetto" larga chiudi={chiudi}>
          <ModuloEnti progetto={p} annulla={chiudi} salvato={salvato} />
        </Modale>
      )}
      {p && finestra?.tipo === 'territorio' && (
        <Modale titolo="Modifica territorio e sedi CIDIS" larga chiudi={chiudi}>
          <ModuloTerritorio progetto={p} annulla={chiudi} salvato={salvato} />
        </Modale>
      )}
      {p && finestra?.tipo === 'destinatari' && (
        <Modale titolo="Modifica i destinatari" larga chiudi={chiudi}>
          <ModuloDestinatari progetto={p} annulla={chiudi} salvato={salvato} />
        </Modale>
      )}
      {p && finestra?.tipo === 'wp' && (
        <Modale titolo={finestra.wp ? `Modifica ${finestra.wp.codice}` : 'Aggiungi una WP'} larga chiudi={chiudi}>
          <ModuloWp progettoId={p.id} iniziale={finestra.wp} annulla={chiudi} salvato={salvato} />
        </Modale>
      )}
      {p && finestra?.tipo === 'attivita' && (
        <Modale titolo={finestra.attivita ? 'Modifica l’attività'
          : finestra.padre ? `Aggiungi una sottoattività a «${finestra.padre.titolo}»` : `Aggiungi un’attività a ${finestra.wp.codice}`}
                larga chiudi={chiudi}>
          <ModuloAttivita wp={finestra.wp} iniziale={finestra.attivita} padre={finestra.padre}
                          annulla={chiudi} salvato={salvato} />
        </Modale>
      )}
      {p && finestra?.tipo === 'andamento' && (
        <Modale titolo={`Andamento di «${finestra.attivita.titolo}»`} chiudi={chiudi}>
          <ModuloAndamento attivita={finestra.attivita} annulla={chiudi} salvato={salvato} />
        </Modale>
      )}
      {p && finestra?.tipo === 'scadenza' && (
        <Modale titolo={finestra.scadenza ? 'Modifica la scadenza' : 'Aggiungi una scadenza'} chiudi={chiudi}>
          <ModuloScadenza progettoId={p.id} iniziale={finestra.scadenza} annulla={chiudi} salvato={salvato} />
        </Modale>
      )}
      {p && finestra?.tipo === 'stato-scadenza' && (
        <Modale titolo="Stato della scadenza" chiudi={chiudi}>
          <ModuloStatoScadenza scadenza={finestra.scadenza} annulla={chiudi} salvato={salvato} />
        </Modale>
      )}
      {finestra?.tipo === 'conferma' && (
        <Modale titolo={finestra.titolo} chiudi={chiudi} azioni={<>
          <Bottone variante="discreto" onClick={chiudi}>Indietro</Bottone>
          <Bottone variante="critico" inCorso={inCorso} onClick={() => void conferma(finestra)}>{finestra.comando}</Bottone>
        </>}>
          <p>{finestra.testo}</p>
        </Modale>
      )}
    </>
  )
}

function EnteCollegato({ ente }: { ente: EnteBreve }) {
  return (
    <span className="rete-con-etichetta">
      <Link to={`/enti/${ente.id}`}>{nomeEnte(ente)}</Link>
      {!ente.attivo && <Etichetta>disattivato</Etichetta>}
    </span>
  )
}

/** Una WP con le sue attivita': le sottoattivita' seguono la propria attivita', rientrate. */
function BloccoWp({ wp, gestisce, rendiconta, apri }: {
  wp: Wp; gestisce: boolean; rendiconta: boolean; apri: (f: Finestra) => void
}) {
  const aree = useAree()
  const tolta = !!wp.archiviato_il
  const righe = wp.attivita.flatMap((a) => [
    { attivita: a, padre: undefined as Attivita | undefined },
    ...(a.sottoattivita ?? []).map((s) => ({ attivita: s, padre: a })),
  ])
  const conAzioni = !tolta && (gestisce || rendiconta)
  const togli = (percorso: string, titolo: string, testo: string) =>
    apri({ tipo: 'conferma', titolo, comando: titolo, testo, percorso, metodo: 'post' })

  return (
    <section className="rete-wp" aria-label={`${wp.codice} ${wp.titolo}`}>
      <header className="rete-wp-capo">
        <div className="dato-testo">
          <h3 className="rete-wp-titolo">
            <span className="rete-con-etichetta">
              <span className="mono">{wp.codice}</span> {wp.titolo}
              {tolta && <Etichetta tono="attenzione">tolta</Etichetta>}
            </span>
          </h3>
          <span className="piccolo tenue">
            {dataBreve(wp.data_inizio)} – {dataBreve(wp.data_fine)}
          </span>
        </div>
        <span className="riga riga-a-destra">
          <Avanzamento valore={wp.avanzamento} />
          {gestisce && !tolta && (
            <>
              <Bottone variante="discreto" dimensione="piccolo" onClick={() => apri({ tipo: 'wp', wp })}>Modifica</Bottone>
              <Bottone variante="discreto" dimensione="piccolo" icona={<IconaPiu />}
                       onClick={() => apri({ tipo: 'attivita', wp })}>Attività</Bottone>
              <Bottone variante="discreto" dimensione="piccolo" onClick={() => togli(`/wp/${wp.id}/archivia`, `Togli ${wp.codice}`,
                `${wp.codice} e le sue attività escono dal progetto. I dati restano: ripristinando la WP `
                + 'tornano anche le attività tolte insieme a lei.')}>Togli</Bottone>
            </>
          )}
          {gestisce && tolta && (
            <Bottone variante="secondario" dimensione="piccolo" onClick={() => togli(`/wp/${wp.id}/ripristina`,
              `Ripristina ${wp.codice}`, `${wp.codice} torna fra le WP del progetto, con le sue attività.`)}>Ripristina</Bottone>
          )}
        </span>
      </header>
      {wp.tipologie.length > 0 && (
        <div className="riga-etichette">
          {wp.tipologie.map((t) => (
            <span key={t.codice} className="rete-tipologia">
              <EtichettaArea codice={t.area_codice} colore={aree.coloreDi(t.area_codice)} />
              <span className="piccolo">{t.nome}</span>
            </span>
          ))}
        </div>
      )}
      {wp.descrizione && <p className="testo-libero piccolo">{wp.descrizione}</p>}

      <Tabella intestazioni={['Attività', 'Responsabile', 'Periodo', 'Stato', 'Avanzamento', 'Ore svolte / previste', 'Indicatore',
                              ...(conAzioni ? [<span className="solo-lettore" key="azioni">Azioni</span>] : [])]}
               vuoto={<p className="tenue">Nessuna attività in questa WP.</p>}>
        {righe.map(({ attivita: a, padre }) => {
          const aTolta = !!a.archiviata_il
          return (
            <tr key={a.id}>
              <td data-etichetta="Attività">
                <span className={`rete-con-etichetta${padre ? ' rete-sottoattivita' : ''}`}>
                  {a.codice && <span className="mono">{a.codice}</span>}
                  {a.titolo}
                  {aTolta && <Etichetta tono="attenzione">tolta</Etichetta>}
                </span>
              </td>
              <td data-etichetta="Responsabile">{nomePersona(a.responsabile) ?? <span className="tenue">—</span>}</td>
              <td data-etichetta="Periodo" className="piccolo">{dataBreve(a.data_inizio)} – {dataBreve(a.data_fine)}</td>
              <td data-etichetta="Stato"><Etichetta tono={STATI_ATTIVITA[a.stato].tono}>{STATI_ATTIVITA[a.stato].etichetta}</Etichetta></td>
              <td data-etichetta="Avanzamento"><Avanzamento valore={a.avanzamento} /></td>
              <td data-etichetta="Ore svolte / previste" className="mono">{cifre(a.ore_svolte) ?? '—'} / {cifre(a.ore_previste) ?? '—'}</td>
              <td data-etichetta="Indicatore" className="piccolo">
                {a.indicatore_nome
                  ? `${a.indicatore_nome}${a.indicatore_target != null ? `: ${cifre(a.indicatore_target)}` : ''}${a.indicatore_unita ? ` ${a.indicatore_unita}` : ''}`
                  : <span className="tenue">—</span>}
              </td>
              {conAzioni && (
                <td data-etichetta="Azioni">
                  <span className="riga riga-a-destra">
                    {!aTolta && rendiconta && (
                      <Bottone variante="discreto" dimensione="piccolo"
                               onClick={() => apri({ tipo: 'andamento', attivita: a })}>Andamento</Bottone>
                    )}
                    {!aTolta && gestisce && (
                      <Bottone variante="discreto" dimensione="piccolo"
                               onClick={() => apri({ tipo: 'attivita', wp, attivita: a })}>Modifica</Bottone>
                    )}
                    {!aTolta && gestisce && !padre && (
                      <Bottone variante="discreto" dimensione="piccolo"
                               onClick={() => apri({ tipo: 'attivita', wp, padre: a })}>Sottoattività</Bottone>
                    )}
                    {gestisce && (aTolta
                      ? <Bottone variante="discreto" dimensione="piccolo" onClick={() => togli(`/attivita/${a.id}/ripristina`,
                          'Ripristina l’attività', `«${a.titolo}» torna nella WP${padre ? '' : ', con le sottoattività tolte insieme a lei'}.`)}>
                          Ripristina
                        </Bottone>
                      : <Bottone variante="discreto" dimensione="piccolo" onClick={() => togli(`/attivita/${a.id}/archivia`,
                          'Togli l’attività', `«${a.titolo}» non compare più nella WP${padre ? '' : ' e con lei le sue sottoattività'}. I dati restano e si può ripristinare.`)}>
                          Togli
                        </Bottone>)}
                  </span>
                </td>
              )}
            </tr>
          )
        })}
      </Tabella>
    </section>
  )
}

/**
 * Stato dell'accesso del referente ai dati del progetto (N-1).
 *
 * «Referente» e «vede i dati» sono due cose diverse: il campo anagrafico da solo non dice quale
 * delle due valga, ed e' l'equivoco che la decisione del committente ha chiuso.
 */
function StatoAccessoReferente({ accesso }: { accesso: DettaglioProgetto['referente_accesso'] }) {
  if (!accesso) return null
  if (accesso.stato === 'concesso') {
    return (
      <Etichetta tono="positivo">
        accede ai dati{accesso.scadenza ? ` fino al ${dataBreve(accesso.scadenza)}` : ''}
      </Etichetta>
    )
  }
  if (accesso.stato === 'in_attesa') {
    return <Etichetta tono="attenzione">accesso in attesa di approvazione</Etichetta>
  }
  return <Etichetta>solo dato anagrafico</Etichetta>
}

// --- modulo dei dati generali e del periodo ------------------------------------------------------------

type SezioneProgetto = 'generali' | 'periodo'
const CAMPI_SEZIONE: Record<SezioneProgetto, string[]> = {
  generali: ['titolo', 'codice_cidis', 'codice_finanziatore', 'cup', 'cig', 'fondo_codice', 'fondo_testo',
    'area_principale', 'finalita', 'referente_id'],
  periodo: ['data_inizio', 'data_fine', 'importo_finanziato', 'importo_cofinanziamento',
    'periodicita_rendicontazione'],
}

type ValoriProgetto = {
  titolo: string; codice_cidis: string; codice_finanziatore: string; cup: string; cig: string
  fondo_codice: string; fondo_testo: string; area_principale: string; finalita: string
  data_inizio: string; data_fine: string; importo_finanziato: string; importo_cofinanziamento: string
  periodicita_rendicontazione: string; sedi: string[]
}

function valoriProgetto(d?: DettaglioProgetto): ValoriProgetto {
  return {
    titolo: d?.titolo ?? '', codice_cidis: d?.codice_cidis ?? '', codice_finanziatore: d?.codice_finanziatore ?? '',
    cup: d?.cup ?? '', cig: d?.cig ?? '', fondo_codice: d?.fondo_codice ?? '', fondo_testo: d?.fondo_testo ?? '',
    area_principale: d?.area_principale ?? '', finalita: d?.finalita ?? '',
    data_inizio: d?.data_inizio ?? '', data_fine: d?.data_fine ?? '',
    importo_finanziato: testoDi(d?.importo_finanziato), importo_cofinanziamento: testoDi(d?.importo_cofinanziamento),
    periodicita_rendicontazione: d?.periodicita_rendicontazione ?? '', sedi: (d?.sedi ?? []).map((s) => s.id).sort(),
  }
}

function corpoProgetto(v: ValoriProgetto, referente: Persona | null, conSpecifica: boolean): Record<string, unknown> {
  return {
    titolo: v.titolo.trim(),
    codice_cidis: v.codice_cidis.trim() || null,
    codice_finanziatore: v.codice_finanziatore.trim() || null,
    cup: compatto(v.cup) || null,
    cig: compatto(v.cig) || null,
    fondo_codice: v.fondo_codice || null,
    fondo_testo: conSpecifica ? v.fondo_testo.trim() || null : null,
    area_principale: v.area_principale || null,
    finalita: v.finalita.trim() || null,
    referente_id: referente?.id ?? null,
    data_inizio: v.data_inizio || null,
    data_fine: v.data_fine || null,
    importo_finanziato: numeroDa(v.importo_finanziato),
    importo_cofinanziamento: numeroDa(v.importo_cofinanziamento),
    periodicita_rendicontazione: v.periodicita_rendicontazione || null,
  }
}

/**
 * Dati generali e periodo del progetto. Nell'inserimento compaiono entrambe le sezioni e le sedi CIDIS,
 * che servono a far nascere il progetto nel perimetro di chi lo inserisce; nella modifica una sezione per
 * volta, e parte solo quanto e' cambiato.
 */
export function ModuloProgetto({ iniziale, sezioni, salvato, annulla }: {
  iniziale?: DettaglioProgetto
  sezioni: SezioneProgetto[]
  /** Il messaggio riporta l'esito delle domande sull'accesso del referente, quando ci sono. */
  salvato: (id: string, messaggio?: string) => void
  annulla: () => void
}) {
  const { sedi, tassonomia, puo } = useSessione()
  const periodicita = useCatalogo('periodicita_rendicontazione')
  const fondi = useVoci<VoceFondo>('/riferimenti/cataloghi/fondo')
  const [v, setV] = useState<ValoriProgetto>(() => valoriProgetto(iniziale))
  const [referente, setReferente] = useState<Persona | null>(iniziale?.referente ?? null)
  const [errori, setErrori] = useState<Record<string, string>>({})
  // Casella di posta dedicata (504): solo nell'inserimento; dopo, dalla sezione «Posta del progetto».
  const [conCasella, setConCasella] = useState(false)
  const [casella, setCasella] = useState<ValoreCasella>(() => casellaIniziale(''))
  const [erroriCasella, setErroriCasella] = useState<Record<string, string>>({})
  const [candidati, setCandidati] = useState<Candidato[] | null>(null)
  useEffect(() => {
    if (!conCasella || candidati !== null) return
    api.get<Candidato[]>('/progetti/utenti-selezionabili?tipo=tutti&limite=200')
      .then(setCandidati).catch(() => setCandidati([]))
  }, [conCasella, candidati])
  // L'identificativo del progetto appena inserito serve alla chiusura del modulo, che avviene nello
  // stesso ciclo della risposta: uno stato non sarebbe ancora aggiornato, un riferimento si'.
  const creato = useRef<string | null>(null)
  // L'esito delle domande sull'accesso arriva con la risposta del salvataggio e va mostrato a
  // chi ha compilato: «concesso» e «richiesto a chi puo'» sono due cose diverse.
  const esitoAccesso = useRef<string | null>(null)
  /** Corpo gia' pronto, in attesa delle risposte alle domande sul referente (N-1). */
  const [domande, setDomande] = useState<{
    corpo: Record<string, unknown>; nuovo: Persona | null; precedente: Persona | null
  } | null>(null)
  const { errore, erroriApi, inCorso, invia, rifiuta, radice } =
    useInvio(() => salvato(iniziale?.id ?? creato.current ?? '', esitoAccesso.current ?? undefined))
  const tutti: Record<string, string> = { ...erroriApi, ...errori }
  const imposta = <K extends keyof ValoriProgetto>(chiave: K, valore: ValoriProgetto[K]) =>
    setV((attuali) => ({ ...attuali, [chiave]: valore }))

  const generali = sezioni.includes('generali')
  const periodo = sezioni.includes('periodo')
  const voceFondo = fondi.find((f) => f.codice === v.fondo_codice)
  const conSpecifica = voceFondo ? voceFondo.richiede_testo
    : v.fondo_codice !== '' && v.fondo_codice === iniziale?.fondo_codice && !!iniziale.fondo_richiede_testo
  const opzioniFondo = conVoce(fondi.filter((f) => f.attivo).map((f) => ({ valore: f.codice, etichetta: f.nome })),
    iniziale?.fondo_codice, iniziale?.fondo_nome)
  const opzioniAree = conVoce((tassonomia?.aree ?? []).filter((a) => a.attiva).map((a) => ({ valore: a.codice, etichetta: a.nome })),
    iniziale?.area_principale, iniziale?.area_nome)
  const opzioniPeriodicita = conVoce(periodicita.voci.map((x) => ({ valore: x.codice, etichetta: x.nome })),
    iniziale?.periodicita_rendicontazione, iniziale?.periodicita_nome)
  const statoPrevisto = statoDalleDate(v.data_inizio || null, v.data_fine || null)

  function convalida(): Record<string, string> {
    const e: Record<string, string> = {}
    if (generali) {
      if (v.titolo.trim().length < 2) e.titolo = 'Indicare il titolo del progetto.'
      if (compatto(v.cup) && !/^[A-Z0-9]{15}$/.test(compatto(v.cup))) e.cup = 'Il CUP è di 15 caratteri tra lettere e cifre.'
      if (compatto(v.cig) && !/^[A-Z0-9]{10}$/.test(compatto(v.cig))) e.cig = 'Il CIG è di 10 caratteri tra lettere e cifre.'
      if (conSpecifica && !v.fondo_testo.trim()) e.fondo_testo = 'Specificare il fondo.'
    }
    if (periodo) {
      if (v.data_inizio && v.data_fine && v.data_fine < v.data_inizio) e.data_fine = 'La data di fine non può precedere la data di inizio.'
      for (const campo of ['importo_finanziato', 'importo_cofinanziamento'] as const) {
        if (v[campo].trim() && !IMPORTO.test(v[campo].trim())) e[campo] = AIUTO_IMPORTO
      }
    }
    return e
  }

  /** Invia il corpo gia' completo delle risposte alle domande sul referente. */
  async function inviaCorpo(corpo: Record<string, unknown>) {
    esitoAccesso.current = null
    if (!iniziale) {
      await invia(async () => {
        const r = await api.post<{
          id: string; accesso_referente: EsitoAccessoReferente | null; casella: { indirizzo: string } | null
        }>('/progetti', corpo)
        creato.current = r.id
        esitoAccesso.current = [r.accesso_referente?.messaggio,
          r.casella && `Casella di posta ${r.casella.indirizzo} creata.`].filter(Boolean).join(' ') || null
      })
      return
    }
    await invia(async () => {
      const r = await api.patch<{
        accesso_referente: EsitoAccessoReferente | null
        revoca_precedente: EsitoAccessoReferente | null
      }>(`/progetti/${iniziale.id}`, corpo)
      esitoAccesso.current = [r.revoca_precedente?.messaggio, r.accesso_referente?.messaggio]
        .filter(Boolean).join(' ') || null
    })
  }

  async function salva() {
    const trovati = convalida()
    setErrori(trovati)
    // Il nome della casella, se non lo si scrive, e' il codice CIDIS o il titolo del progetto.
    const valoreCasella = { ...casella, nome: casella.nome.trim() || (v.codice_cidis.trim() || v.titolo.trim()).slice(0, 120) }
    const trovatiCasella = !iniziale && conCasella ? controllaCasella(valoreCasella) : {}
    setErroriCasella(trovatiCasella)
    if (Object.keys(trovati).length || Object.keys(trovatiCasella).length) {
      rifiuta('Correggere i campi segnalati.'); return
    }

    const completo = corpoProgetto(v, referente, conSpecifica)
    let corpo: Record<string, unknown> = Object.fromEntries(
      sezioni.flatMap((s) => CAMPI_SEZIONE[s]).map((c) => [c, completo[c]]))
    if (!iniziale) {
      corpo.sedi = [...v.sedi].sort()
      if (conCasella) corpo.casella = corpoCasella(valoreCasella)
      // N-1: il referente appena indicato porta con se' la domanda sull'accesso ai dati.
      if (referente) { setDomande({ corpo, nuovo: referente, precedente: null }); return }
      await inviaCorpo(corpo)
      return
    }
    const prima = corpoProgetto(valoriProgetto(iniziale), iniziale.referente, !!iniziale.fondo_richiede_testo)
    corpo = Object.fromEntries(Object.entries(corpo).filter(([k, x]) => !uguali(x, prima[k])))
    // Fondo e specifica partono insieme: l'API controlla la coppia.
    if ('fondo_codice' in corpo || 'fondo_testo' in corpo) {
      corpo.fondo_codice = completo.fondo_codice
      corpo.fondo_testo = completo.fondo_testo
    }
    if (!Object.keys(corpo).length) { salvato(iniziale.id); return }
    // Il cambio di referente fa una domanda per il nuovo e una per il precedente: senza
    // risposta l'API rifiuta il salvataggio, e ha ragione.
    if ('referente_id' in corpo) {
      setDomande({ corpo, nuovo: referente, precedente: iniziale.referente ?? null })
      return
    }
    await inviaCorpo(corpo)
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>

      {generali && (
        <>
          <h3 className="rete-titoletto">Identificazione</h3>
          <div className="griglia-modulo">
            <Campo etichetta="Titolo" obbligatorio maxLength={400} valore={v.titolo}
                   cambia={(x) => imposta('titolo', x)} errore={tutti.titolo} />
            <Campo etichetta="Codice CIDIS del progetto" maxLength={50} valore={v.codice_cidis}
                   cambia={(x) => imposta('codice_cidis', x)} />
            <Campo etichetta="Codice (del finanziatore)" maxLength={80} valore={v.codice_finanziatore}
                   cambia={(x) => imposta('codice_finanziatore', x)} />
            <Campo etichetta="CUP" maxLength={20} valore={v.cup} cambia={(x) => imposta('cup', x)}
                   errore={tutti.cup} aiuto="15 caratteri tra lettere e cifre." />
            <Campo etichetta="CIG" maxLength={14} valore={v.cig} cambia={(x) => imposta('cig', x)}
                   errore={tutti.cig} aiuto="10 caratteri tra lettere e cifre." />
          </div>

          <h3 className="rete-titoletto">Fondo, area e finalità</h3>
          <div className="griglia-modulo">
            <Scelta etichetta="Fondo" vuota="— nessuno —" valore={v.fondo_codice} opzioni={opzioniFondo}
                    cambia={(x) => imposta('fondo_codice', x)} />
            {conSpecifica && (
              <Campo etichetta="Specifica del fondo" obbligatorio maxLength={200} valore={v.fondo_testo}
                     cambia={(x) => imposta('fondo_testo', x)} errore={tutti.fondo_testo} />
            )}
            <Scelta etichetta="Area principale" vuota="— nessuna —" valore={v.area_principale} opzioni={opzioniAree}
                    cambia={(x) => imposta('area_principale', x)} aiuto="Facoltativa." />
          </div>
          <AreaTesto etichetta="Obiettivo o finalità generale" righe={3} maxLength={2000} valore={v.finalita}
                     cambia={(x) => imposta('finalita', x)} />

          {/* Obiettivi FAMI, ruolo privacy, data del saldo e controlli del finanziatore non si
              chiedono piu' dal modulo (Osservazioni 3, punto 4: anagrafica troppo complessa). Le
              colonne restano in banca dati con i valori gia' scritti. */}

          <h3 className="rete-titoletto">Referente CIDIS</h3>
          <SceltaUtente etichetta="Referente CIDIS" etichettaRicerca="Cerca il referente CIDIS" vuota="— nessuno —"
                        valore={referente} cambia={setReferente} errore={tutti.referente_id}
                        aiuto="Dato anagrafico e destinatario dei promemoria di rendicontazione: non apre l’accesso ai dati del progetto. Apre invece la casella di posta del progetto, se c’è." />
        </>
      )}

      {periodo && (
        <>
          <h3 className="rete-titoletto">Periodo, importi e rendicontazione</h3>
          <div className="griglia-modulo">
            <Campo etichetta="Data inizio" tipo="date" valore={v.data_inizio} cambia={(x) => imposta('data_inizio', x)} />
            <Campo etichetta="Data fine" tipo="date" valore={v.data_fine} cambia={(x) => imposta('data_fine', x)}
                   errore={tutti.data_fine}
                   aiuto={statoPrevisto ? `Stato calcolato dalle date: ${STATI_PROGETTO[statoPrevisto].etichetta}.` : 'Lo stato si calcola dalle date.'} />
            <Campo etichetta="Importo finanziato (euro)" maxLength={14} valore={v.importo_finanziato}
                   cambia={(x) => imposta('importo_finanziato', x)} errore={tutti.importo_finanziato} />
            <Campo etichetta="Importo cofinanziamento (euro)" maxLength={14} valore={v.importo_cofinanziamento}
                   cambia={(x) => imposta('importo_cofinanziamento', x)} errore={tutti.importo_cofinanziamento} />
            <Scelta etichetta="Periodicità di rendicontazione" vuota="— non indicata —" valore={v.periodicita_rendicontazione}
                    opzioni={opzioniPeriodicita} cambia={(x) => imposta('periodicita_rendicontazione', x)} />
          </div>
        </>
      )}

      {!iniziale && (
        <fieldset className="gruppo-caselle">
          <legend>Sedi CIDIS</legend>
          {sedi.length
            ? <Caselle voci={vociSedi(sedi)} scelte={v.sedi} cambia={(x) => imposta('sedi', x)} />
            : <p className="tenue">Nessuna sede CIDIS attiva.</p>}
        </fieldset>
      )}

      {!iniziale && (
        <>
          <h3 className="rete-titoletto">Casella di posta del progetto</h3>
          <Spunta etichetta="Il progetto ha una casella di posta dedicata" valore={conCasella} cambia={setConCasella}
                  aiuto="Una casella del progetto, senza inoltri: si apre dalla pagina Posta o dalla pagina del progetto. Si può creare anche dopo." />
          {conCasella && (
            <SceltaCasellaProgetto valore={casella} cambia={setCasella} candidati={candidati}
                                   puoEsistente={puo('utente.gestisci')} errori={erroriCasella}
                                   nomeVuoto="il codice CIDIS del progetto, o il titolo" />
          )}
        </>
      )}

      <AzioniModulo annulla={annulla} inCorso={inCorso} etichetta={iniziale ? 'Salva le modifiche' : 'Inserisci il progetto'}
                    errore={errore} errori={tutti} />

      {domande && (
        <DomandeReferente
          nuovo={domande.nuovo} precedente={domande.precedente}
          puoAssegnare={iniziale?.permessi.assegna ?? false}
          inCorso={inCorso}
          annulla={() => setDomande(null)}
          conferma={async (risposte) => {
            const corpo = { ...domande.corpo, ...risposte }
            setDomande(null)
            await inviaCorpo(corpo)
          }} />
      )}
    </form>
  )
}

/**
 * Le due domande del referente (N-1).
 *
 * Chi compila il progetto non deve dedurre dall'interfaccia se il referente vedra' i dati: glielo
 * si chiede, e la risposta dice anche se l'accesso si aprira' subito o dovra' passare da
 * un'approvazione. La motivazione e' obbligatoria in entrambi i versi e resta nel registro.
 */
function DomandeReferente({ nuovo, precedente, puoAssegnare, inCorso, conferma, annulla }: {
  nuovo: Persona | null
  precedente: Persona | null
  puoAssegnare: boolean
  inCorso: boolean
  conferma: (risposte: Record<string, unknown>) => void | Promise<void>
  annulla: () => void
}) {
  const [concedi, setConcedi] = useState<'si' | 'no' | ''>('')
  const [motivoAccesso, setMotivoAccesso] = useState('')
  const [revoca, setRevoca] = useState<'si' | 'no' | ''>('')
  const [motivoRevoca, setMotivoRevoca] = useState('')

  const manca = [
    nuovo && !concedi && 'la risposta sull’accesso del nuovo referente',
    nuovo && concedi === 'si' && motivoAccesso.trim().length < 5 && 'la motivazione dell’accesso',
    precedente && !revoca && 'la risposta sulla revoca del referente precedente',
    precedente && revoca === 'si' && motivoRevoca.trim().length < 5 && 'la motivazione della revoca',
  ].filter(Boolean) as string[]

  const immediato = puoAssegnare
    ? 'L’accesso si apre subito, con il tuo nome sull’incarico.'
    : 'Non puoi assegnare incarichi su questo progetto: partirà una richiesta di approvazione a chi può, e l’accesso si aprirà solo dopo.'

  return (
    <Modale titolo="Accesso del referente ai dati del progetto" chiudi={annulla}
            azioni={(
              <>
                <Bottone variante="discreto" onClick={annulla}>Annulla</Bottone>
                <Bottone variante="primario" inCorso={inCorso} disabled={manca.length > 0}
                         onClick={() => void conferma({
                           ...(nuovo
                             ? { accesso_referente: {
                               concedi: concedi === 'si',
                               motivazione: concedi === 'si' ? motivoAccesso.trim() : null,
                             } }
                             : {}),
                           ...(precedente
                             ? { revoca_precedente: {
                               revoca: revoca === 'si',
                               motivazione: revoca === 'si' ? motivoRevoca.trim() : null,
                             } }
                             : {}),
                         })}>
                  Salva il progetto
                </Bottone>
              </>
            )}>
      {precedente && (
        <section>
          <p>
            <strong>Revocare a {nomePersona(precedente)} l’accesso ai dati del progetto?</strong>
          </p>
          <p className="piccolo tenue">
            Si tolgono solo gli incarichi nati da questa domanda. Gli altri incarichi sullo stesso
            progetto restano: li si toglie da Amministrazione → Utenti.
          </p>
          <Scelta etichetta="Revoca l’accesso" obbligatorio valore={revoca} vuota="Scegli…"
                  cambia={(x) => setRevoca(x as 'si' | 'no' | '')}
                  opzioni={[{ valore: 'si', etichetta: 'Sì, revoca l’accesso' },
                    { valore: 'no', etichetta: 'No, lascia l’accesso' }]} />
          {revoca === 'si' && (
            <AreaTesto etichetta="Motivo della revoca" obbligatorio righe={2}
                       valore={motivoRevoca} cambia={setMotivoRevoca}
                       aiuto="Resta sull’incarico revocato e nel registro degli eventi." />
          )}
        </section>
      )}

      {nuovo && (
        <section className={precedente ? 'referente-blocco' : undefined}>
          <p>
            <strong>Concedere a {nomePersona(nuovo)} l’accesso ai dati del progetto?</strong>
          </p>
          <p className="piccolo tenue">
            Essere referente è un dato anagrafico e da solo non apre nulla. {immediato}
          </p>
          <Scelta etichetta="Concedi l’accesso" obbligatorio valore={concedi} vuota="Scegli…"
                  cambia={(x) => setConcedi(x as 'si' | 'no' | '')}
                  opzioni={[{ valore: 'si', etichetta: 'Sì, concedi l’accesso' },
                    { valore: 'no', etichetta: 'No, solo dato anagrafico' }]} />
          {concedi === 'si' && (
            <AreaTesto etichetta="Motivo dell’accesso" obbligatorio righe={2}
                       valore={motivoAccesso} cambia={setMotivoAccesso}
                       aiuto="Resta sull’incarico e nel registro degli eventi." />
          )}
        </section>
      )}

      {manca.length > 0 && <p className="nota-modulo">Da completare: {manca.join(', ')}.</p>}
    </Modale>
  )
}

// --- enti del progetto -----------------------------------------------------------------------------------

type VocePartner = { ente: EnteScelto; ruolo: string; ruolo_descrizione: string }
const corpoPartner = (lista: VocePartner[]) =>
  lista.map((x) => ({ stakeholder_id: x.ente.id, ruolo: x.ruolo, ruolo_descrizione: x.ruolo_descrizione.trim() || null }))

function ModuloEnti({ progetto, salvato, annulla }: { progetto: DettaglioProgetto; salvato: () => void; annulla: () => void }) {
  const iniziali: VocePartner[] = progetto.partner
    .filter((x) => RUOLI_PARTNER.some((r) => r.valore === x.ruolo))
    .map((x) => ({ ente: { id: x.stakeholder_id, nome: x.nome, sigla: x.sigla }, ruolo: x.ruolo, ruolo_descrizione: x.ruolo_descrizione ?? '' }))
  const [capofila, setCapofila] = useState<EnteScelto | null>(progetto.capofila)
  const [finanziatore, setFinanziatore] = useState<EnteScelto | null>(progetto.finanziatore)
  const [partner, setPartner] = useState<VocePartner[]>(iniziali)
  const [nuovo, setNuovo] = useState<EnteScelto | null>(null)
  const [ruoloNuovo, setRuoloNuovo] = useState('partner')
  // Il doppione si segnala sul ruolo, accanto a «Aggiungi il partner»: in testa alla finestra non si vedeva.
  const [erroreAggiunta, setErroreAggiunta] = useState<string | null>(null)
  const { errore, erroriApi, inCorso, invia, radice } = useInvio(salvato)

  const cambiaVoce = (indice: number, modifica: Partial<VocePartner>) =>
    setPartner((a) => a.map((x, i) => (i === indice ? { ...x, ...modifica } : x)))

  function aggiungi() {
    if (!nuovo) return
    if (partner.some((x) => x.ente.id === nuovo.id && x.ruolo === ruoloNuovo)) {
      setErroreAggiunta('Questo ente ha già questo ruolo nel progetto.')
      return
    }
    setErroreAggiunta(null)
    setPartner((a) => [...a, { ente: nuovo, ruolo: ruoloNuovo, ruolo_descrizione: '' }])
    setNuovo(null)
  }

  async function salva() {
    const corpo: Record<string, unknown> = {}
    if ((capofila?.id ?? null) !== (progetto.capofila?.id ?? null)) corpo.capofila_id = capofila?.id ?? null
    if ((finanziatore?.id ?? null) !== (progetto.finanziatore?.id ?? null)) corpo.finanziatore_id = finanziatore?.id ?? null
    if (!uguali(corpoPartner(partner), corpoPartner(iniziali))) corpo.partner = corpoPartner(partner)
    if (!Object.keys(corpo).length) { salvato(); return }
    await invia(() => api.patch(`/progetti/${progetto.id}`, corpo))
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <h3 className="rete-titoletto">Capofila e finanziatore</h3>
      <SceltaEnte etichetta="Ente capofila" vuota="— nessuno —" valore={capofila} cambia={setCapofila}
                  errore={erroriApi.capofila_id} />
      <SceltaEnte etichetta="Ente finanziatore" vuota="— nessuno —" valore={finanziatore} cambia={setFinanziatore}
                  errore={erroriApi.finanziatore_id} />

      <h3 className="rete-titoletto">Partner</h3>
      {partner.length === 0 && <p className="tenue">Nessun partner.</p>}
      {partner.map((x, i) => (
        <div key={`${x.ente.id}:${i}`} className="rete-voce-modulo">
          <span className="rete-voce-nome">{nomeEnte(x.ente)}</span>
          <Scelta etichetta="Ruolo" valore={x.ruolo} opzioni={RUOLI_PARTNER} cambia={(r) => cambiaVoce(i, { ruolo: r })} />
          <Campo etichetta="Ruolo nel progetto" maxLength={300} valore={x.ruolo_descrizione}
                 cambia={(d) => cambiaVoce(i, { ruolo_descrizione: d })} segnaposto="Facoltativo" />
          <Bottone variante="discreto" dimensione="piccolo" onClick={() => setPartner((a) => a.filter((_, j) => j !== i))}>
            Togli
          </Bottone>
        </div>
      ))}
      <SceltaEnte etichetta="Ente da aggiungere fra i partner" vuota="— scegliere un ente —" valore={nuovo}
                  cambia={(e) => { setNuovo(e); setErroreAggiunta(null) }} />
      <div className="rete-voce-modulo">
        <Scelta etichetta="Ruolo dell’ente da aggiungere" valore={ruoloNuovo} opzioni={RUOLI_PARTNER}
                cambia={(r) => { setRuoloNuovo(r); setErroreAggiunta(null) }} errore={erroreAggiunta} />
        <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />} disabled={!nuovo} onClick={aggiungi}>
          Aggiungi il partner
        </Bottone>
      </div>
      <AzioniModulo annulla={annulla} inCorso={inCorso} etichetta="Salva gli enti" errore={errore} errori={erroriApi} />
    </form>
  )
}

// --- territorio e sedi ------------------------------------------------------------------------------------

function ModuloTerritorio({ progetto, salvato, annulla }: { progetto: DettaglioProgetto; salvato: () => void; annulla: () => void }) {
  const { sedi } = useSessione()
  const [comuni, setComuni] = useState<ComuneScelto[]>(progetto.comuni)
  const [scelto, setScelto] = useState<ComuneScelto | null>(null)
  const [sediScelte, setSediScelte] = useState<string[]>(progetto.sedi.map((s) => s.id))
  const { errore, erroriApi, inCorso, invia, radice } = useInvio(salvato)

  const opzioniSedi = [
    ...vociSedi(sedi),
    // Una sede collegata e poi chiusa non e' fra quelle attive: resta visibile e si puo' togliere.
    ...progetto.sedi.filter((s) => !sedi.some((a) => a.id === s.id)).map((s) => ({ codice: s.id, nome: `${s.nome} · non attiva` })),
  ]
  const codici = (lista: { codice_istat: string }[]) => lista.map((c) => c.codice_istat).sort()

  async function salva() {
    const corpo: Record<string, unknown> = {}
    if (!uguali(codici(comuni), codici(progetto.comuni))) corpo.comuni = codici(comuni)
    if (!uguali([...sediScelte].sort(), progetto.sedi.map((s) => s.id).sort())) corpo.sedi = [...sediScelte].sort()
    if (!Object.keys(corpo).length) { salvato(); return }
    await invia(() => api.patch(`/progetti/${progetto.id}`, corpo))
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <h3 className="rete-titoletto">Comuni di realizzazione delle attività</h3>
      {comuni.length === 0 && <p className="tenue">Nessun comune.</p>}
      {comuni.map((c) => (
        <div key={c.codice_istat} className="rete-voce-modulo">
          <span className="rete-voce-nome">{c.nome}{c.provincia_sigla ? ` (${c.provincia_sigla})` : ''}</span>
          <Bottone variante="discreto" dimensione="piccolo"
                   onClick={() => setComuni((a) => a.filter((x) => x.codice_istat !== c.codice_istat))}>Togli</Bottone>
        </div>
      ))}
      <SceltaComune etichetta="Comune da aggiungere" vuota="— scegliere un comune —" valore={scelto} cambia={setScelto}
                    errore={erroriApi.comuni} />
      <div className="rete-voce-modulo">
        <Bottone variante="secondario" dimensione="piccolo" icona={<IconaPiu />} disabled={!scelto}
                 onClick={() => {
                   if (scelto && !comuni.some((c) => c.codice_istat === scelto.codice_istat)) setComuni((a) => [...a, scelto])
                   setScelto(null)
                 }}>
          Aggiungi il comune
        </Bottone>
      </div>

      <fieldset className={`gruppo-caselle${erroriApi.sedi ? ' campo-errato' : ''}`}>
        <legend>Sedi CIDIS</legend>
        {opzioniSedi.length
          ? <Caselle voci={opzioniSedi} scelte={sediScelte} cambia={setSediScelte} />
          : <p className="tenue">Nessuna sede CIDIS attiva.</p>}
        {erroriApi.sedi && <p className="campo-errore" role="alert">{erroriApi.sedi}</p>}
      </fieldset>
      <AzioniModulo annulla={annulla} inCorso={inCorso} etichetta="Salva territorio e sedi" errore={errore} errori={erroriApi} />
    </form>
  )
}

// --- destinatari ----------------------------------------------------------------------------------------

function ModuloDestinatari({ progetto, salvato, annulla }: { progetto: DettaglioProgetto; salvato: () => void; annulla: () => void }) {
  const [testo, setTesto] = useState(progetto.destinatari_testo ?? '')
  const { errore, erroriApi, inCorso, invia, radice } = useInvio(salvato)

  async function salva() {
    const dopo = testo.trim() || null
    if (dopo === (progetto.destinatari_testo ?? null)) { salvato(); return }
    await invia(() => api.patch(`/progetti/${progetto.id}`, { destinatari_testo: dopo }))
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      {/* Osservazioni 3, punto 6: i destinatari sono la frase del bando, non un conteggio per
          categoria. Per MISCA: «Cittadini dei Paesi Terzi della UE richiedenti o titolari di
          protezione internazionale». */}
      <AreaTesto etichetta="Destinatari" righe={3} maxLength={500} valore={testo} cambia={setTesto}
                 errore={erroriApi.destinatari_testo}
                 aiuto="Come li scrive il progetto, in una frase breve." />
      <AzioniModulo annulla={annulla} inCorso={inCorso} etichetta="Salva i destinatari" errore={errore}
                    errori={erroriApi} />
    </form>
  )
}

// --- work package e attivita' ---------------------------------------------------------------------------------

function ModuloWp({ progettoId, iniziale, salvato, annulla }: {
  progettoId: string; iniziale?: Wp; salvato: () => void; annulla: () => void
}) {
  const { tassonomia } = useSessione()
  const valori = (w?: Wp) => ({
    codice: w?.codice ?? '', titolo: w?.titolo ?? '', descrizione: w?.descrizione ?? '',
    data_inizio: w?.data_inizio ?? '', data_fine: w?.data_fine ?? '', tipologie: (w?.tipologie ?? []).map((t) => t.codice).sort(),
  })
  const [v, setV] = useState(() => valori(iniziale))
  const [errori, setErrori] = useState<Record<string, string>>({})
  const { errore, erroriApi, inCorso, invia, rifiuta, radice } = useInvio(salvato)
  const tutti: Record<string, string> = { ...erroriApi, ...errori }
  const imposta = (chiave: keyof typeof v, valore: string | string[]) => setV((a) => ({ ...a, [chiave]: valore }))

  // Tipologie raggruppate per area; quelle chiuse restano visibili solo se gia' collegate alla WP.
  const gruppi = (tassonomia?.aree ?? []).map((area) => ({
    area,
    voci: (tassonomia?.tipologie ?? [])
      .filter((t) => t.area_codice === area.codice
        && ((t.attiva && !t.storica) || iniziale?.tipologie.some((x) => x.codice === t.codice)))
      .map((t) => ({ codice: t.codice, nome: t.attiva && !t.storica ? t.nome : `${t.nome} · non più in uso` })),
  })).filter((g) => g.voci.length)

  const corpo = (x: ReturnType<typeof valori>) => ({
    codice: x.codice.trim() || undefined, titolo: x.titolo.trim(), descrizione: x.descrizione.trim() || null,
    data_inizio: x.data_inizio || null, data_fine: x.data_fine || null, tipologie: [...x.tipologie].sort(),
  })

  async function salva() {
    const e: Record<string, string> = {}
    if (v.titolo.trim().length < 2) e.titolo = 'Indicare il titolo della WP.'
    if (iniziale && !v.codice.trim()) e.codice = 'Il codice della WP non può restare vuoto.'
    if (v.data_inizio && v.data_fine && v.data_fine < v.data_inizio) e.data_fine = 'La data di fine non può precedere la data di inizio.'
    // PRJ-27: la WP si riferisce ad almeno una tipologia. Il controllo vero lo fa l'API; qui si
    // evita di far compilare tutto il modulo per poi rimandare indietro l'errore.
    if (!v.tipologie.length) e.tipologie = 'Indicare almeno una tipologia di intervento.'
    setErrori(e)
    if (Object.keys(e).length) { rifiuta('Correggere i campi segnalati.'); return }
    if (!iniziale) {
      await invia(() => api.post(`/progetti/${progettoId}/wp`, corpo(v)))
      return
    }
    const prima = corpo(valori(iniziale))
    const dopo = corpo(v)
    const modifiche = Object.fromEntries(Object.entries(dopo).filter(([k, x]) => !uguali(x, prima[k as keyof typeof prima])))
    if (!Object.keys(modifiche).length) { salvato(); return }
    await invia(() => api.patch(`/wp/${iniziale.id}`, modifiche))
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <div className="griglia-modulo">
        <Campo etichetta="Codice" maxLength={30} valore={v.codice} cambia={(x) => imposta('codice', x)} errore={tutti.codice}
               aiuto={iniziale ? undefined : 'Lasciare vuoto per il numero successivo: WP1, WP2, WP3…'} />
        <Campo etichetta="Titolo" obbligatorio maxLength={300} valore={v.titolo} cambia={(x) => imposta('titolo', x)} errore={tutti.titolo} />
        <Campo etichetta="Data inizio" tipo="date" valore={v.data_inizio} cambia={(x) => imposta('data_inizio', x)} />
        <Campo etichetta="Data fine" tipo="date" valore={v.data_fine} cambia={(x) => imposta('data_fine', x)} errore={tutti.data_fine} />
      </div>
      <AreaTesto etichetta="Descrizione" righe={3} maxLength={4000} valore={v.descrizione} cambia={(x) => imposta('descrizione', x)} />
      <h3 className="rete-titoletto">Tipologie di intervento a cui la WP si riferisce</h3>
      {tutti.tipologie && <Avviso tipo="errore">{tutti.tipologie}</Avviso>}
      {gruppi.map((g) => (
        <fieldset key={g.area.codice} className="gruppo-caselle">
          <legend>{g.area.nome}</legend>
          <Caselle voci={g.voci} scelte={v.tipologie} cambia={(x) => imposta('tipologie', x)} />
        </fieldset>
      ))}
      <AzioniModulo annulla={annulla} inCorso={inCorso} etichetta={iniziale ? 'Salva la WP' : 'Aggiungi la WP'}
                    errore={errore} errori={tutti} />
    </form>
  )
}

function ModuloAttivita({ wp, iniziale, padre, salvato, annulla }: {
  wp: Wp; iniziale?: Attivita; padre?: Attivita; salvato: () => void; annulla: () => void
}) {
  const valori = (a?: Attivita) => ({
    codice: a?.codice ?? '', titolo: a?.titolo ?? '', data_inizio: a?.data_inizio ?? '', data_fine: a?.data_fine ?? '',
    indicatore_nome: a?.indicatore_nome ?? '', indicatore_target: testoDi(a?.indicatore_target),
    indicatore_unita: a?.indicatore_unita ?? '', ore_previste: testoDi(a?.ore_previste),
  })
  const [v, setV] = useState(() => valori(iniziale))
  const [responsabile, setResponsabile] = useState<Persona | null>(iniziale?.responsabile ?? null)
  const [errori, setErrori] = useState<Record<string, string>>({})
  const { errore, erroriApi, inCorso, invia, rifiuta, radice } = useInvio(salvato)
  const tutti: Record<string, string> = { ...erroriApi, ...errori }
  const imposta = (chiave: keyof typeof v, valore: string) => setV((a) => ({ ...a, [chiave]: valore }))

  const corpo = (x: ReturnType<typeof valori>, r: Persona | null) => ({
    codice: x.codice.trim() || null, titolo: x.titolo.trim(), responsabile_id: r?.id ?? null,
    data_inizio: x.data_inizio || null, data_fine: x.data_fine || null,
    indicatore_nome: x.indicatore_nome.trim() || null, indicatore_target: numeroDa(x.indicatore_target),
    indicatore_unita: x.indicatore_unita.trim() || null, ore_previste: numeroDa(x.ore_previste),
  })

  async function salva() {
    const e: Record<string, string> = {}
    if (v.titolo.trim().length < 2) e.titolo = 'Indicare il titolo dell’attività.'
    if (v.data_inizio && v.data_fine && v.data_fine < v.data_inizio) e.data_fine = 'La data di fine non può precedere la data di inizio.'
    if (v.ore_previste.trim() && !ORE.test(v.ore_previste.trim())) e.ore_previste = 'Ore con al massimo due decimali.'
    if (v.indicatore_target.trim() && !IMPORTO.test(v.indicatore_target.trim())) e.indicatore_target = 'Un numero non negativo con al massimo due decimali.'
    setErrori(e)
    if (Object.keys(e).length) { rifiuta('Correggere i campi segnalati.'); return }
    if (!iniziale) {
      await invia(() => api.post(`/wp/${wp.id}/attivita`, { ...corpo(v, responsabile), padre_id: padre?.id ?? null }))
      return
    }
    const prima = corpo(valori(iniziale), iniziale.responsabile)
    const dopo = corpo(v, responsabile)
    const modifiche = Object.fromEntries(Object.entries(dopo).filter(([k, x]) => !uguali(x, prima[k as keyof typeof prima])))
    if (!Object.keys(modifiche).length) { salvato(); return }
    await invia(() => api.patch(`/attivita/${iniziale.id}`, modifiche))
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <div className="griglia-modulo">
        <Campo etichetta="Codice" maxLength={30} valore={v.codice} cambia={(x) => imposta('codice', x)} aiuto="Facoltativo: A1, A1.1…" />
        <Campo etichetta="Titolo" obbligatorio maxLength={300} valore={v.titolo} cambia={(x) => imposta('titolo', x)} errore={tutti.titolo} />
        <Campo etichetta="Data inizio" tipo="date" valore={v.data_inizio} cambia={(x) => imposta('data_inizio', x)} />
        <Campo etichetta="Data fine" tipo="date" valore={v.data_fine} cambia={(x) => imposta('data_fine', x)} errore={tutti.data_fine} />
        <Campo etichetta="Ore previste" maxLength={10} valore={v.ore_previste} cambia={(x) => imposta('ore_previste', x)} errore={tutti.ore_previste} />
      </div>
      <SceltaUtente etichetta="Responsabile" etichettaRicerca="Cerca il responsabile" vuota="— nessuno —"
                    valore={responsabile} cambia={setResponsabile} errore={tutti.responsabile_id} />
      <h3 className="rete-titoletto">Indicatore</h3>
      <div className="griglia-modulo">
        <Campo etichetta="Indicatore" maxLength={200} valore={v.indicatore_nome} cambia={(x) => imposta('indicatore_nome', x)} />
        <Campo etichetta="Valore obiettivo" maxLength={14} valore={v.indicatore_target} cambia={(x) => imposta('indicatore_target', x)} errore={tutti.indicatore_target} />
        <Campo etichetta="Unità" maxLength={50} valore={v.indicatore_unita} cambia={(x) => imposta('indicatore_unita', x)} />
      </div>
      <AzioniModulo annulla={annulla} inCorso={inCorso} etichetta={iniziale ? 'Salva l’attività' : 'Aggiungi'}
                    errore={errore} errori={tutti} />
    </form>
  )
}

/** Stato, avanzamento e ore svolte: la parte dell'attivita' che aggiorna chi rendiconta. */
function ModuloAndamento({ attivita, salvato, annulla }: { attivita: Attivita; salvato: () => void; annulla: () => void }) {
  const [stato, setStato] = useState<string>(attivita.stato)
  const [avanzamento, setAvanzamento] = useState(String(attivita.avanzamento))
  const [ore, setOre] = useState(testoDi(attivita.ore_svolte))
  const [errori, setErrori] = useState<Record<string, string>>({})
  const { errore, erroriApi, inCorso, invia, rifiuta, radice } = useInvio(salvato)
  const tutti: Record<string, string> = { ...erroriApi, ...errori }

  async function salva() {
    const e: Record<string, string> = {}
    if (!/^\d{1,3}$/.test(avanzamento.trim()) || Number(avanzamento) > 100) e.avanzamento = 'Un intero da 0 a 100.'
    if (ore.trim() && !ORE.test(ore.trim())) e.ore = 'Ore con al massimo due decimali.'
    setErrori(e)
    if (Object.keys(e).length) { rifiuta('Correggere i campi segnalati.'); return }
    const corpo: Record<string, unknown> = {}
    if (stato !== attivita.stato) corpo.stato = stato
    if (Number(avanzamento) !== attivita.avanzamento) corpo.avanzamento = Number(avanzamento)
    if (numeroDa(ore) !== (attivita.ore_svolte ?? null)) corpo.ore_svolte = numeroDa(ore)
    if (!Object.keys(corpo).length) { salvato(); return }
    await invia(() => api.patch(`/attivita/${attivita.id}`, corpo))
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <div className="griglia-modulo">
        <Scelta etichetta="Stato" valore={stato} opzioni={opzioniDa(STATI_ATTIVITA)} cambia={setStato} />
        <Campo etichetta="Avanzamento (%)" tipo="number" min={0} max={100} passi={5} modalitaTastiera="numeric"
               valore={avanzamento} cambia={setAvanzamento} errore={tutti.avanzamento} />
        <Campo etichetta="Ore svolte" maxLength={10} valore={ore} cambia={setOre} errore={tutti.ore} />
      </div>
      <AzioniModulo annulla={annulla} inCorso={inCorso} etichetta="Salva l’andamento" errore={errore} errori={tutti} />
    </form>
  )
}

// --- scadenze -------------------------------------------------------------------------------------------------

function ModuloScadenza({ progettoId, iniziale, salvato, annulla }: {
  progettoId: string; iniziale?: Scadenza; salvato: () => void; annulla: () => void
}) {
  const [descrizione, setDescrizione] = useState(iniziale?.descrizione ?? '')
  const [scadenza, setScadenza] = useState(iniziale?.scadenza ?? '')
  const [preavviso, setPreavviso] = useState(String(iniziale?.promemoria_giorni ?? 15))
  const [responsabile, setResponsabile] = useState<Persona | null>(iniziale?.responsabile ?? null)
  const [errori, setErrori] = useState<Record<string, string>>({})
  const { errore, erroriApi, inCorso, invia, rifiuta, radice } = useInvio(salvato)
  const tutti: Record<string, string> = { ...erroriApi, ...errori }

  async function salva() {
    const e: Record<string, string> = {}
    if (descrizione.trim().length < 2) e.descrizione = 'Descrivere la scadenza.'
    if (!scadenza) e.scadenza = 'Indicare la data della scadenza.'
    if (!/^\d{1,2}$/.test(preavviso.trim()) || Number(preavviso) < 1 || Number(preavviso) > 90) e.preavviso = 'Da 1 a 90 giorni.'
    setErrori(e)
    if (Object.keys(e).length) { rifiuta('Correggere i campi segnalati.'); return }
    const dopo = {
      descrizione: descrizione.trim(), scadenza, responsabile_id: responsabile?.id ?? null, promemoria_giorni: Number(preavviso),
    }
    if (!iniziale) {
      await invia(() => api.post(`/progetti/${progettoId}/scadenze`, dopo))
      return
    }
    const prima = {
      descrizione: iniziale.descrizione, scadenza: iniziale.scadenza, responsabile_id: iniziale.responsabile?.id ?? null,
      promemoria_giorni: iniziale.promemoria_giorni,
    }
    const modifiche = Object.fromEntries(Object.entries(dopo).filter(([k, x]) => !uguali(x, prima[k as keyof typeof prima])))
    if (!Object.keys(modifiche).length) { salvato(); return }
    await invia(() => api.patch(`/scadenze/${iniziale.id}`, modifiche))
  }

  return (
    <form ref={radice} onSubmit={(e) => { e.preventDefault(); void salva() }} noValidate>
      <Campo etichetta="Descrizione" obbligatorio maxLength={300} valore={descrizione} cambia={setDescrizione}
             errore={tutti.descrizione} segnaposto="Rendiconto intermedio, relazione trimestrale…" />
      <div className="griglia-modulo">
        <Campo etichetta="Data della scadenza" tipo="date" obbligatorio valore={scadenza} cambia={setScadenza} errore={tutti.scadenza} />
        <Campo etichetta="Preavviso del promemoria (giorni)" tipo="number" min={1} max={90} modalitaTastiera="numeric"
               valore={preavviso} cambia={setPreavviso} errore={tutti.preavviso} />
      </div>
      <SceltaUtente etichetta="Responsabile" etichettaRicerca="Cerca il responsabile" vuota="— nessuno —"
                    valore={responsabile} cambia={setResponsabile} errore={tutti.responsabile_id}
                    aiuto="Facoltativo. Il promemoria va comunque al referente CIDIS del progetto." />
      <AzioniModulo annulla={annulla} inCorso={inCorso} etichetta={iniziale ? 'Salva la scadenza' : 'Aggiungi la scadenza'}
                    errore={errore} errori={tutti} />
    </form>
  )
}

function ModuloStatoScadenza({ scadenza, salvato, annulla }: { scadenza: Scadenza; salvato: () => void; annulla: () => void }) {
  const [stato, setStato] = useState<string>(scadenza.stato)
  const { errore, erroriApi, inCorso, invia, radice } = useInvio(salvato)
  return (
    <form ref={radice} onSubmit={(e) => {
      e.preventDefault()
      if (stato === scadenza.stato) { salvato(); return }
      void invia(() => api.patch(`/scadenze/${scadenza.id}`, { stato }))
    }} noValidate>
      <p className="nota-modulo">{scadenza.descrizione} — {dataBreve(scadenza.scadenza)}</p>
      <Scelta etichetta="Stato" valore={stato} opzioni={opzioniDa(STATI_SCADENZA)} cambia={setStato}
              errore={erroriApi.stato} aiuto="Una scadenza consegnata non genera più promemoria." />
      <AzioniModulo annulla={annulla} inCorso={inCorso} etichetta="Salva lo stato" errore={errore} errori={erroriApi} />
    </form>
  )
}
